import { KeycloakTokenParsed } from "keycloak-js";

// Keycloak realm roles granted to the `sync_admin` group (backend:
// com.vngrs.syncrosale.role.AdminRole). Holding any of these shows the admin
// group in the sidebar.
export const ADMIN_ROLES = {
  LIST_SELLERS: "LIST_SELLERS",
  DEACTIVATE_SELLERS: "DEACTIVATE_SELLERS",
  VIEW_FEATURE_FLAGS: "VIEW_FEATURE_FLAGS",
  MANAGE_FEATURE_FLAGS: "MANAGE_FEATURE_FLAGS",
  VIEW_WAREHOUSES: "VIEW_WAREHOUSES",
  MANAGE_WAREHOUSES: "MANAGE_WAREHOUSES",
  VIEW_SURCHARGES: "VIEW_SURCHARGES",
  MANAGE_SURCHARGES: "MANAGE_SURCHARGES",
  TRIGGER_PRODUCT_SYNC: "TRIGGER_PRODUCT_SYNC",
  MANAGE_ACTUATOR: "MANAGE_ACTUATOR",
  MANAGE_SELLER_SUSPENSION: "MANAGE_SELLER_SUSPENSION",
} as const;

export type AdminAccess = {
  isAdmin: boolean;
  canViewFeatureFlags: boolean;
  canManageFeatureFlags: boolean;
  canViewWarehouses: boolean;
  canManageWarehouses: boolean;
  canViewSurcharges: boolean;
  canManageSurcharges: boolean;
  canTriggerProductSync: boolean;
  canManageActuator: boolean;
  canManageSellerSuspension: boolean;
};

// First admin page the user may open; where an admin without a store lands.
export const getAdminHome = (access: AdminAccess): string => {
  if (access.canViewFeatureFlags) return "/admin/feature-flags";
  if (access.canViewWarehouses) return "/admin/warehouses";
  if (access.canTriggerProductSync) return "/admin/product-sync";
  if (access.canManageSellerSuspension) return "/admin/sellers";
  if (access.canManageActuator) return "/admin/system";
  return "/admin/feature-flags";
};

export const getAdminAccess = (
  tokenParsed: KeycloakTokenParsed | undefined,
): AdminAccess => {
  const roles = tokenParsed?.realm_access?.roles ?? [];
  const adminRoles: string[] = Object.values(ADMIN_ROLES);

  return {
    isAdmin: roles.some((role) => adminRoles.includes(role)),
    canViewFeatureFlags: roles.includes(ADMIN_ROLES.VIEW_FEATURE_FLAGS),
    canManageFeatureFlags: roles.includes(ADMIN_ROLES.MANAGE_FEATURE_FLAGS),
    canViewWarehouses: roles.includes(ADMIN_ROLES.VIEW_WAREHOUSES),
    canManageWarehouses: roles.includes(ADMIN_ROLES.MANAGE_WAREHOUSES),
    canViewSurcharges: roles.includes(ADMIN_ROLES.VIEW_SURCHARGES),
    canManageSurcharges: roles.includes(ADMIN_ROLES.MANAGE_SURCHARGES),
    canTriggerProductSync: roles.includes(ADMIN_ROLES.TRIGGER_PRODUCT_SYNC),
    canManageActuator: roles.includes(ADMIN_ROLES.MANAGE_ACTUATOR),
    canManageSellerSuspension: roles.includes(
      ADMIN_ROLES.MANAGE_SELLER_SUSPENSION,
    ),
  };
};
